import { withFinancialMetricsAccess } from "@crm-src/components/page-permission-guard"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { ChipProps } from "@mui/material"
import { PageLoader } from "@/components/ui/page-loader"
import { usePermissions } from "@crm-src/hooks/usePermissions"
import { useAppSelector } from "@crm-src/lib/store"
import { financialApi } from "@crm-src/lib/api"
import { formatCurrency } from "@crm-src/lib/utils"
import { useToast } from "@crm-src/hooks/use-toast"
import { DollarSign, Target, TrendingUp, FileText, Calendar } from "lucide-react"
import {
  IndividualPerformanceData,
  PeriodType
} from "@crm-src/types/financial-metrics"

// Import components
import { TimeRangeSelector } from "@crm-src/components/financial-metrics/time-range-selector"
import { StatsCard } from "@crm-src/components/stats-card"
import { SalespersonPerformanceCharts } from "@crm-src/components/financial-metrics/salesperson-performance-charts"
import { BackButton } from "@/components/ui/back-button";

function SalespersonDetail() {
  const params = useParams()
  const navigate = useNavigate()
  const userId = params.id as string
  
  const [period, setPeriod] = useState<PeriodType>('1M')
  const [data, setData] = useState<IndividualPerformanceData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  const { canAccessAdmin } = usePermissions()
  const { toast } = useToast()
  const user = useAppSelector((state) => state.auth.user)
  const isAdmin = canAccessAdmin()

  // Access control: admin can view anyone, salesperson can only view themselves
  useEffect(() => {
    if (!isAdmin && user?.id?.toString() !== userId) {
      toast({
        title: "Access Denied",
        description: "You can only view your own performance metrics",
        variant: "destructive",
      })
      navigate('/crm/financial-metrics')
    }
  }, [isAdmin, user?.id, userId, navigate, toast])

  // Load data
  useEffect(() => {
    loadData()
  }, [period, userId])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const response = await financialApi.getSalespersonDetails(userId, { period })
      setData(response.data)
    } catch (error: any) {
      console.error('Error loading salesperson details:', error)
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to load salesperson details",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Maps an invoice/quote status to a MUI Chip palette color (inlined during
  // the MUI 5 migration from the former Tailwind status-class badges).
  const getStatusChipColor = (status: string): ChipProps['color'] => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success'
      case 'pending':
        return 'warning'
      case 'converted':
        return 'info'
      case 'unpaid':
        return 'warning'
      case 'partial':
        return 'secondary'
      case 'expired':
        return 'default'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const handleBack = () => {
    navigate('/crm/financial-metrics')
  }

  if (isLoading) {
    return <PageLoader />
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography sx={{ color: "text.secondary" }}>No data available</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={handleBack}>
          Go Back
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Simple Header: Back Button, Name, Time Filter */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isAdmin && (
            <BackButton
              label="Back to Salespeople"
              onClick={() => navigate(`/crm/financial-metrics/salespeople?period=${period}`)}
            />
          )}
          <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.025em" }}>{data.user.name}</Typography>
        </Box>
        <TimeRangeSelector selected={period} onChange={setPeriod} />
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(data.performance.total_revenue, { decimals: 0 })}
          icon={DollarSign}
          iconColor="text-green-600"
          valueColor="text-green-600"
        />

        <StatsCard
          title="Deals Closed"
          value={data.performance.total_deals_closed}
          icon={Target}
          iconColor="text-blue-600"
          valueColor="text-blue-600"
        />

        <StatsCard
          title="Conversion Rate"
          value={`${data.performance.conversion_rate}%`}
          icon={TrendingUp}
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />

        <StatsCard
          title="Pending Quotes"
          value={data.performance.quotes_pending}
          icon={FileText}
          iconColor="text-orange-600"
          valueColor="text-orange-600"
        />
      </Box>

      {/* Monthly Breakdown Table */}
      {data.monthly_breakdown && data.monthly_breakdown.length > 0 && (
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Calendar size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>Monthly Performance Breakdown</Typography>
              </Box>
            }
            subheader="Detailed monthly performance data"
          />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Deals Closed</TableCell>
                  <TableCell align="right">Quotes Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.monthly_breakdown.map((month) => (
                  <TableRow key={month.month}>
                    <TableCell sx={{ fontWeight: 500 }}>{month.month}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>
                      {formatCurrency(month.revenue, { decimals: 0 })}
                    </TableCell>
                    <TableCell align="right">{month.deals_closed}</TableCell>
                    <TableCell align="right">{month.quotes_created}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Performance Charts */}
      {data.monthly_breakdown && data.monthly_breakdown.length > 0 && (
        <SalespersonPerformanceCharts 
          monthlyBreakdown={data.monthly_breakdown}
          isLoading={isLoading}
        />
      )}

      {/* Recent Invoices */}
      {data.invoices && data.invoices.length > 0 && (
        <Card>
          <CardHeader
            title={<Typography component="span" sx={{ fontWeight: 600 }}>Recent Invoices</Typography>}
            subheader={`Last ${data.invoices.length} invoices created in this period`}
          />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell sx={{ fontWeight: 500 }}>{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.customer_name}</TableCell>
                    <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                    <TableCell>
                      <Chip size="small" color={getStatusChipColor(invoice.status) ?? "default"} label={invoice.status} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(invoice.total_amount, { decimals: 0 })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recent Quotes */}
      {data.quotes && data.quotes.length > 0 && (
        <Card>
          <CardHeader
            title={<Typography component="span" sx={{ fontWeight: 600 }}>Recent Quotes</Typography>}
            subheader={`Last ${data.quotes.length} quotes created in this period`}
          />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Quote #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.quotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell sx={{ fontWeight: 500 }}>{quote.invoice_number}</TableCell>
                    <TableCell>{quote.customer_name}</TableCell>
                    <TableCell>{formatDate(quote.issue_date)}</TableCell>
                    <TableCell>
                      <Chip size="small" color={getStatusChipColor(quote.status) ?? "default"} label={quote.status} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(quote.total_amount, { decimals: 0 })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Additional Insights */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
        <Card sx={{ borderColor: "#bfdbfe", bgcolor: "#eff6ff" }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.875rem", fontWeight: 700, color: "#1e3a8a" }}>
                {formatCurrency(data.performance.average_deal_size, { decimals: 0 })}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "#1d4ed8", mt: 1 }}>Average Deal Size</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: "#bbf7d0", bgcolor: "#f0fdf4" }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.875rem", fontWeight: 700, color: "#14532d" }}>
                {data.performance.quotes_converted}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "#15803d", mt: 1 }}>Quotes Converted</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: "#e9d5ff", bgcolor: "#faf5ff" }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.875rem", fontWeight: 700, color: "#581c87" }}>
                {data.performance.total_quotes_created}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "#7e22ce", mt: 1 }}>Total Quotes Created</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default withFinancialMetricsAccess(SalespersonDetail)

